<mat-toolbar class="crud-table-main-toolbar">
  <div class="app-card-title-container">
    <span class="crud-table-main-toolbar-title">{{ title }}</span>
    <span class="mat-h3 no-margin crud-table-main-toolbar-subtitle">{{ subTitle }}</span>
  </div>
  <span class="flex-spacer"></span>

  <div *ngIf="tableData && showLoadMore" class="crud-table-load-more-container">
    <span class="body-3-default">Loaded {{ tableData.length }} items</span>
    <button
      mat-icon-button
      class="crud-table-main-toolbar-icon"
      matTooltip="Load more"
      [disabled]="!canLoadMoreData || !!(loading$ | async)"
      (click)="loadMoreData()"
    >
      <mat-icon class="crud-table-main-toolbar-icon remove-icon">
        docs_add_on
      </mat-icon>
    </button>
  </div>

  <button *ngIf="showReload"
    mat-icon-button
    class="crud-table-main-toolbar-icon"
    matTooltip="Refresh Data"
    [disabled]="!!(loading$ | async)"
    (click)="refreshData()"
  >
    <mat-icon class="crud-table-main-toolbar-icon remove-icon">
      refresh
    </mat-icon>
  </button>

  <button
    mat-icon-button
    matTooltip="Advanced search"
    (click)="toggleInfoPanel()"
  >
    <mat-icon class="crud-table-main-toolbar-icon">help</mat-icon>
  </button>

  <button
    *ngIf="hasNonDefaultSearchTerms()"
    mat-icon-button
    class="crud-table-main-toolbar-icon"
    matTooltip="Clear all filters"
    (click)="clearSearchTerms()"
  >
    <mat-icon
      class="crud-table-main-toolbar-icon remove-icon"
      [matBadge]="searchTerms.length"
      matBadgeSize="small"
      matBadgeColor="warn"
    >filter_alt_off
    </mat-icon
    >
  </button>

  <button
    mat-icon-button
    [matMenuTriggerFor]="mainOptionsMenu"
    matTooltip="Table options"
  >
    <mat-icon class="crud-table-main-toolbar-icon">more_vert</mat-icon>
  </button>

  <mat-menu #mainOptionsMenu="matMenu">
    @if (additionalActions.length > 0) {
      @for (action of additionalActions; track action) {
        <button mat-menu-item
                [disabled]="!isCustomMenuItemEnabled(action)"
                (click)="onAdditionalActionSelect(action)"
        >
          <mat-icon>{{ action.icon }}</mat-icon>
          <span>{{ action.label }}</span>
        </button>
      }
      <mat-divider></mat-divider>
    }

    <!-- Select columns -->
    <button mat-menu-item (click)="openColsDialog()">
      <mat-icon>view_column_2</mat-icon>
      <span>Select columns</span>
    </button>

    <!-- Export to CSV -->
    <button mat-menu-item (click)="exportToCsv()">
      <mat-icon>file_download</mat-icon>
      <span>Export to CSV</span>
    </button>

    <!-- Export to Excel -->
    <button mat-menu-item (click)="exportToExcel()">
      <mat-icon>file_download</mat-icon>
      <span>Export to Excel</span>
    </button>

    <!-- Export to PDF -->
    <button mat-menu-item (click)="exportToPdf()">
      <mat-icon>file_download</mat-icon>
      <span>Export to PDF</span>
    </button>
  </mat-menu>

</mat-toolbar>

@if (showLoaderBars && (loading$ | async)) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

@if (longDescription) {
  <div class="mat-body-large long-description">{{ longDescription }}</div>
}

<ng-container *ngIf="filterType === 'MATERIAL'">
  <mat-form-field class="app-table-chip-list">
    <mat-chip-grid #chipGrid>
      @for (term of searchTerms; track term; let i = $index) {
        <mat-chip-row color="primary"
                      class="app-table-chip"
                      (removed)="removeSearchTerm(i)"
                      [editable]="false"
        >
          {{ term }}
          <button matChipRemove>
            <mat-icon class="app-table-chip-list-icon">cancel</mat-icon>
          </button>
        </mat-chip-row>
      }
      <input
        placeholder="Filter..."
        [matChipInputFor]="chipGrid"
        [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
        [matChipInputAddOnBlur]="true"
        (matChipInputTokenEnd)="applyMaterialFilter($event)"
      />
    </mat-chip-grid>
  </mat-form-field>
</ng-container>

<ng-template *ngIf="filterType === 'NATIVE'" #nativeFilter>
  <div class="search-terms-container">
    <mat-icon class="search-icon" (click)="clearSearchTerms()">search</mat-icon>
    <div class="search-terms">
      <span *ngFor="let term of searchTerms; let i = index" class="search-term">
        {{ term }}
        <mat-icon class="remove-icon" (click)="removeSearchTerm(i)"
        >close</mat-icon
        >
      </span>

      <input
        matInput
        placeholder="Filter ..."
        (keydown.enter)="applyNativeFilter($event)"
        class="crud-search-input"
      />
    </div>
  </div>
</ng-template>

<ui-kit-expanding-info-panel *ngIf="showMoreInfoMessage"
                             class="crud-table-expanding-help-panel"
                             [type]="PanelType.INFO"
                             [fullWidth]="true"
                             (onDisplayExpanded)="moreInfoMessage = $event"
                             [buttons]="true"
                             [buttonLabels]="['Close']"
                             (emitEvent)="toggleInfoPanel(false)">
  @if(moreInfoMessage){
    <p class="pt-5-i">
      <strong>Search tips</strong> Add one or more search filters. All filters are combined (the row must match all of them). Search terms are case sensitive.
    </p>

    <span><strong>Simple search</strong></span>
    <ul>
      <li><strong>Any text</strong> searches across all visible columns.</li>
      <li><strong>Example:</strong> <code class="no-bg-color-i">acme</code></li>
    </ul>


    <p>
      <strong>Search a specific column</strong> - Use the column name and value in the following format: <code class="no-bg-color-i">column_name:value</code>.
    </p>
    <ul>
      <li><strong>Exact match:</strong> <code class="no-bg-color-i">Status:Active</code></li>
      <li><strong>Contains:</strong> <code class="no-bg-color-i">Name.contains:john</code></li>
      <li><strong>One of a list:</strong> <code class="no-bg-color-i">Type.in:Retail,Wholesale</code></li>
    </ul>

    <span><strong>Numbers</strong></span>
    <ul>
      <li><strong>Larger than:</strong><code class="no-bg-color-i">number.min:10</code></li>
      <li><strong>Less than:</strong><code class="no-bg-color-i">number.max:100</code></li>
      <li><strong>Between:</strong><code class="no-bg-color-i">number.between:10 and 20</code></li>
    </ul>


    <p>
      <strong>Dates</strong> - Search for dates using the following format: <code class="no-bg-color-i">YYYY-MM-DD</code> for dates.
    </p>

    <ul>
      <li><strong>After:</strong><code class="no-bg-color-i">date.after:2024-01-01</code></li>
      <li><strong>Before:</strong><code class="no-bg-color-i">date.before:2024-12-31</code></li>
      <li><strong>Between:</strong><code class="no-bg-color-i">date.between:2024-01-01 and 2024-03-31</code></li>
    </ul>
  }@else{
    <p class="pt-5-i">
      Add one or more filters. Rows must match all filters to appear.
      Use plain text to search all visible columns, or target a column like
      <code>Status:Active</code> / <code>Name.contains:john</code>. You can also filter by
      <code>number.min</code>, <code>number.max</code>, <code>number.between</code> and
      <code>date.after</code>, <code>date.before</code>, <code>date.between</code>. Search terms are case sensitive.
    </p>
  }
</ui-kit-expanding-info-panel>

<div class="crud-table-scroll-container">
  <mat-table
    [dataSource]="dataSource"
    matSort
    cdkDropList
    cdkDropListOrientation="horizontal"
    (cdkDropListDropped)="drop($event)"
  >
    <ng-container
      *ngFor="let column of displayColumns"
      [matColumnDef]="column.colName"
    >
      <mat-header-cell *matHeaderCellDef mat-sort-header cdkDrag>
        {{ column.displayLabel | uppercase }}
      </mat-header-cell>

      <mat-cell *matCellDef="let element"
                [ngClass]="{'clickable-cell': column.clickFunction}"
                [matTooltip]="column.clickFunction ? (column.clickTooltip || '') : ''"
                (click)="column.clickFunction ? column.clickFunction(element[column.colName]) : null"
      >
        {{ column.displayFunction ? column.displayFunction(element[column.colName]) : element[column.colName] }}
      </mat-cell>
    </ng-container>

    <div class="nodata-row" *matNoDataRow>
      <mat-icon class="nodata-icon">info</mat-icon>

      @if (loadDataSubscription) {
        <span class="mat-h2 nodata-header">Loading...</span>
        <span class="mat-body nodata-text">Retrieving data from the server, this may take a few moments</span>
      } @else {
        <span class="mat-h2 nodata-header">No data found</span>
        <span class="mat-body nodata-text">Try adjusting any filters or reloading the data</span>
      }
    </div>

    <mat-header-row
      *matHeaderRowDef="visibleColumns | pluck: 'colName'; sticky: true"
    ></mat-header-row>
    <mat-row
      *matRowDef="let row; columns: visibleColumns | pluck: 'colName'"
      (click)="onRowClicked(row)"
      [class.crud-table-row-highlighted]="row === selectedRow"
      class="app-table-row-un-highlighted"
    ></mat-row>
  </mat-table>
</div>
<mat-paginator
  #crudPaginator
  [pageSizeOptions]="[15, 25, 50, 100]"
></mat-paginator>
